import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Tag,
  TablePagination,
  Tooltip,
  defaultTheme,
  Typography,
  Alert,
  IconButton,
  COLORS,
} from 'pvs-design-system';
import { Box, Stack } from '@mui/material';
import { EditNote } from '@mui/icons-material';
import styled from '@emotion/styled';
import { useGetToolsListQuery } from 'api';
import getUserAccess from 'utils/userAccessMapper';
import { ToolsQuery } from 'types';
import LoadingSpinner from 'components/LoadingSpinner/LoadingSpinner';

const StyledEditNote = styled(EditNote)({
  transform: 'scale(1.3331)',
  padding: '0 0 .2rem .2rem',
  color: COLORS.purple[700],
});

const ToolsTable: React.FC = () => {
  const navigate = useNavigate();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const {
    data: tools,
    isLoading,
    isFetching,
    isError,
  } = useGetToolsListQuery({}) as ToolsQuery;

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  const StyledTag = styled(Tag)`
    span {
      color: ${defaultTheme?.palette.text.primary};
    }
  `;

  return (
    <>
      <LoadingSpinner
        displayText="Loading LTI 1.3 Tools"
        showLoading={isLoading}
      />

      {!isFetching && (
        <>
          <TableContainer data-testid="tools-table-container">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Tool Name</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>Permissions</TableCell>
                  <TableCell>User Access</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {!isError &&
                  tools
                    ?.slice(
                      page * rowsPerPage,
                      page * rowsPerPage + rowsPerPage
                    )
                    .map((item) => {
                      return (
                        <TableRow key={item.id} sx={{ height: '92px' }}>
                          <TableCell sx={{ minWidth: '12rem' }}>
                            <Box title={item.id}>{item.id}</Box>
                          </TableCell>
                          <TableCell
                            sx={{ minWidth: '12rem', maxWidth: '24rem' }}
                          >
                            <Box title={item.description}>
                              {item.description}
                            </Box>
                          </TableCell>
                          <TableCell sx={{ minWidth: '12rem' }}>
                            <Box
                              title={item.dynamicParameter.permissions
                                .map(
                                  (permission) =>
                                    `${permission.manage ?? ''}${
                                      permission.launch ?? ''
                                    }`
                                )
                                .join('\n')}
                            >
                              {item.dynamicParameter.permissions.map(
                                (permission) => (
                                  <Box
                                    key={`tool_permissions_${
                                      item.id
                                    }_${crypto.randomUUID()}`}
                                  >
                                    <Box>{permission.manage ?? ''}</Box>
                                    <Box>{permission.launch ?? ''}</Box>
                                  </Box>
                                )
                              )}
                            </Box>
                          </TableCell>
                          <TableCell
                            sx={{ minWidth: '12rem', whiteSpace: 'nowrap' }}
                          >
                            {getUserAccess(item.authorization) !== '' && (
                              <StyledTag
                                variant="outlined"
                                label={getUserAccess(item.authorization)}
                                color="success"
                              />
                            )}
                          </TableCell>
                          <TableCell align="center" sx={{ padding: 0 }}>
                            <Tooltip title="View and Edit Tool">
                              <IconButton
                                id={`button_edit_tool_${item.id}`}
                                aria-label="Edit"
                                onClick={() =>
                                  navigate(
                                    `/ltimanagement/tool/${item.id}/edit`
                                  )
                                }
                              >
                                <StyledEditNote />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      );
                    })}
              </TableBody>
            </Table>
          </TableContainer>
          {!isError && tools?.length > 0 && (
            <TablePagination
              rowsPerPageOptions={[5, 10, 25, 100]}
              component="div"
              count={tools?.length || 0}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          )}
          {!isError && (!tools || tools.length === 0) && (
            <Box sx={{ marginTop: '32px' }}>
              <Stack alignItems="center" data-testid="no-items-found">
                <Typography variant="p-md">No results found.</Typography>
              </Stack>
            </Box>
          )}
          {isError && (
            <Box sx={{ marginTop: '32px' }}>
              <Alert severity="error" data-testid="error">
                We encountered an error loading LTI 1.3 tools. Please try again.
              </Alert>
            </Box>
          )}
        </>
      )}
    </>
  );
};

export default ToolsTable;
